import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';

import { ThemeProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { Settings as LuxonSettings } from 'luxon';

import { Header } from 'commons/components/Header';
import { Loading } from 'commons/components/Loading';
import { Snackbar } from 'commons/components/Snackbar';
import {
  getDashboardAlertNotification,
  getUserAlertNotification,
} from 'redux-stores/actions';
import { RootReducerInterface } from 'redux-stores/reducers';
import { snackbarSetOpen } from 'redux-stores/reducers/utilityReducer';
import { AppDispatch } from 'redux-stores/store';

import './pdfjs-setup';
import { AppRouter } from './routes';
import { DASHBOARD_ROUTE_ROOT } from './routes/constants/dashboard-routes.constant';
import theme from './theme-provider';

const App = (): React.JSX.Element => {
  // FIXME probably better to load from env?
  LuxonSettings.defaultZone = 'Asia/Jakarta';
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const skipAlertNotification = searchParams.get('skip-alert-notif');

  const onSnackbarClose = () => dispatch(snackbarSetOpen(false));

  const snackbar = useSelector(
    (state: RootReducerInterface) => state.utility.snackbar,
  );

  const loading = useSelector(
    (state: RootReducerInterface) => state.utility.loading,
  );

  const isDashboardPage = location.pathname.startsWith(DASHBOARD_ROUTE_ROOT);

  useEffect(() => {
    if (
      (!skipAlertNotification || skipAlertNotification !== 'true') &&
      !isDashboardPage
    ) {
      dispatch(getUserAlertNotification());
    }
  }, [dispatch, skipAlertNotification, isDashboardPage]);

  useEffect(() => {
    if (isDashboardPage) {
      dispatch(getDashboardAlertNotification());
    }
  }, [dispatch, isDashboardPage]);

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <ThemeProvider theme={theme}>
        <Header />
        <AppRouter />
        <Snackbar
          color={snackbar?.color}
          message={snackbar?.message}
          autoHideDuration={snackbar?.autoHideDuration}
          open={snackbar?.open}
          onClose={onSnackbarClose}
          style={{ whiteSpace: 'pre-line' }}
        />
        <Loading show={loading.show} />
      </ThemeProvider>
    </LocalizationProvider>
  );
};

export default App;
